// ============================================================================
// Operations — the ops team's home for everything in flight: the two trip-planning
// BOARDS — First Mile (inbound: supplier pickups and WH↔WH transfers) and Last
// Mile (outbound: shipments, dropships, and returns coming back), with the
// outstation part-load work split across the two by leg — each grouped by
// section → trip / owner and scoped to a dispatch day, the manual-provider
// confirmation inbox, and the two warehouse work queues (pending picklist /
// pending GRN).
//
// Both boards lead with an ESCALATIONS section: a truck an operator has raised an
// escalation on is lifted out of to-assign / on-a-truck and worked from there,
// with the issue and the note on the row, until someone resolves it. Escalations
// live on the board rather than in a tab of their own because they are the same
// trucks, filtered by their own filters and searchable in the same bar.
//
// Last Mile leads with a MISSING ORDERS section — rendered as a section OF that board, by the board's
// own table and cells, not as a tab or a panel bolted above it. The board is rooted on trucks, so it
// can only show work that EXISTS; an allotment whose last leg never got a truck (stuck in a picklist
// error, waiting on a first mile that never arrived) has nothing on the board to be late, and the
// team that owes the customer the delivery never sees it. The section is that inverse question, it
// names the leg actually blocking each row, and it honours the board's filters — so it narrows with
// the controls above it instead of ignoring them. It appears only when there is something in it.
//
// The tab you land on is the one you LEFT (remembered per browser — see `remembered_tab.ts`). That is
// not the thing that was deliberately removed: Operations used to JUMP to Pending Tasks whenever that
// inbox was non-empty, so the view you got depended on the state of one queue — you could not learn
// where things are, and a deep link to the board re-routed itself out from under you. Remembering the
// operator's own last choice is the opposite of that; it is stable, and it is theirs.
//
// SEARCH leads the tabs and is the default for anyone who has never picked one. It is the same board,
// over both lanes at once, that fetches nothing until something is typed: looking up one truck by PO
// or carrier ref is the single most common reason anyone opens this screen, and a board load is the
// most expensive query here (unpaginated, a whole lane — expensive enough that polling had to be
// removed), so spending one on a lane the operator scrolls straight past was the wrong default.
// ============================================================================
import { CarOutlined } from "@ant-design/icons";
import { Badge, Tabs } from "antd";
import React, { useState } from "react";
import styles from "../index.module.scss";
import { useRememberedTab } from "../remembered_tab";
import Board from "./board";
import PendingQueuePanel from "./components/pending_queue_panel";
import PendingTasksPanel from "./components/pending_tasks_panel";
import ReturnsPanel from "../returns/returns_panel";

const CountLabel: React.FC<{ label: string; count: number }> = ({ label, count }) => (
  <Badge count={count} size="small" offset={[12, -2]}>
    <span>{label}</span>
  </Badge>
);

// The Outstation Client tab opens on the customer-bound partner legs — the work that tab exists for.
// Module-level so the board's section memo sees a stable array.
const OUTSTATION_LEAD_SECTIONS = ["last_mile_outstation"];

// Where the last-opened tab is remembered. Scoped to this page, so it cannot collide with any other
// screen's preferences. Every key the Tabs below render must be listed: a stored key that is not in
// here falls back to the default, so forgetting one silently un-remembers that tab.
const TAB_STORAGE_KEY = "oms.operations.tab";
const TAB_KEYS = [
  "search",
  "first_mile",
  "last_mile",
  "outstation_client",
  "picklist",
  "grn",
  "returns",
  "pending",
];
const DEFAULT_TAB = "search";

const OperationsDashboard: React.FC = () => {
  const [tab, setTab] = useRememberedTab(TAB_STORAGE_KEY, TAB_KEYS, DEFAULT_TAB);
  const [pendingCount, setPendingCount] = useState(0);
  const [pickCount, setPickCount] = useState(0);
  const [grnCount, setGrnCount] = useState(0);
  const [returnsCount, setReturnsCount] = useState(0);

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <CarOutlined className={styles.headerIcon} />
        <h2 className={styles.title}>Operations</h2>
      </div>

      <Tabs
        activeKey={tab}
        onChange={setTab}
        items={[
          // One board over BOTH lanes that stays empty until the operator types. No `lane`: a search
          // reaches across both halves server-side, which is exactly what a lookup needs — an
          // outstation order has legs on either board, so nobody can know which tab holds theirs.
          // `showMissingOrders` so a search also surfaces a delivery that never got a truck: that is
          // the order most likely to be looked up and the one no other tab can show.
          { key: "search", label: "Search", children: <Board searchOnly showMissingOrders /> },
          // The board is split in two: goods coming TOWARDS us (supplier pickups, WH↔WH
          // transfers) and goods going OUT to the customer (shipments, dropships, and returns
          // coming back). Different people work the two halves, so each gets its own board
          // rather than one list they have to mentally filter.
          //
          // OUTSTATION (intercity part-load) is not a third lane and belongs to neither: such an
          // order runs a hop that feeds a warehouse and a leg that carries the goods on to the
          // customer, so each half sits on the board whose team works it and BOTH boards show an
          // "Outstation" section. Searching is the one thing that ignores the split — it spans
          // both boards, because nobody can know in advance which one holds the order.
          { key: "first_mile", label: "First Mile", children: <Board lane="first_mile" /> },
          {
            key: "last_mile",
            label: "Last Mile",
            // Outstation clients are worked from their own tab, so they leave this one. Returns
            // leave too — the shared Returns tab below is their home, and surfacing a reverse-leg
            // truck here as well meant one booking in two sections.
            // `showMissingOrders` makes this board lead with the deliveries that were promised and
            // never booked — a section of the board, deliberately NOT a tab or a panel above it (see
            // the note at the top of this file).
            children: <Board lane="last_mile" excludeOutstation excludeReturns showMissingOrders />,
          },
          // Both halves of the board locked to customers no hub serves (off the hub pincode list and
          // over 100 km from every hub) — the rows tagged "Outstation client", pickups and deliveries
          // together. Worked as their own set, so they get a tab rather than a filter someone has to
          // remember to pick. Last Mile drops them (`excludeOutstation`); First Mile still shows its
          // half as well.
          {
            key: "outstation_client",
            label: "Outstation Client",
            children: (
              <Board
                fixedCity="outstation"
                sectionLabels={{
                  first_mile: { outstation: "Outstation First Mile" },
                  last_mile: { outstation: "Outstation Last Mile" },
                }}
                leadSections={OUTSTATION_LEAD_SECTIONS}
                // Picked date + the next 15 days: outstation bookings are planned ahead.
                dispatchWindowDays={15}
              />
            ),
          },
          {
            key: "picklist",
            // forceRender so the panel mounts on load and reports its count — the badge is the
            // signal that there is warehouse work waiting, without opening the tab.
            forceRender: true,
            label: <CountLabel label="Pending Picklist" count={pickCount} />,
            children: <PendingQueuePanel kind="picklist" onCountChange={setPickCount} />,
          },
          {
            key: "grn",
            forceRender: true,
            label: <CountLabel label="Pending GRN" count={grnCount} />,
            children: <PendingQueuePanel kind="grn" onCountChange={setGrnCount} />,
          },
          {
            // Shared Returns worklist (delivery + CS teams view; admin-only authorize button —
            // ADR 2026-10-01). forceRender so the count badge shows without opening the tab.
            key: "returns",
            forceRender: true,
            label: <CountLabel label="Returns" count={returnsCount} />,
            children: <ReturnsPanel onCountChange={setReturnsCount} />,
          },
          {
            key: "pending",
            forceRender: true,
            label: <CountLabel label="Pending Tasks" count={pendingCount} />,
            children: <PendingTasksPanel onCountChange={setPendingCount} onChanged={() => {}} />,
          },
        ]}
      />
    </div>
  );
};

export default OperationsDashboard;
